import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import './style/AdminLogin.css';

const AdminLogin = () => {
    const navigate = useNavigate();
    
    // --- Page State ---
    const [showLoader, setShowLoader] = useState(true);
    const [websiteData, setWebsiteData] = useState({
        logo: '',
        name: 'CashBolt Admin',
        tagline: 'Secure Panel Access'
    });

    // --- Form State ---
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [errors, setErrors] = useState({});
    const [loginLoading, setLoginLoading] = useState(false);

    // --- Captcha State ---
    const [captchaOptions, setCaptchaOptions] = useState([]);
    const [targetCaptcha, setTargetCaptcha] = useState(null);
    const [selectedCaptcha, setSelectedCaptcha] = useState(null);

    // --- Captcha Generator ---
    const generateCaptcha = () => {
        const icons = [
            { id: 1, symbol: '🚀', name: 'Rocket' },
            { id: 2, symbol: '💎', name: 'Diamond' },
            { id: 3, symbol: '⚡', name: 'Bolt' },
            { id: 4, symbol: '🛡️', name: 'Shield' },
            { id: 5, symbol: '🔥', name: 'Fire' },
            { id: 6, symbol: '👑', name: 'Crown' }
        ];
        const shuffled = icons.sort(() => 0.5 - Math.random()).slice(0, 4);
        const target = shuffled[Math.floor(Math.random() * shuffled.length)];
        
        setCaptchaOptions(shuffled);
        setTargetCaptcha(target);
        setSelectedCaptcha(null);
    };

    useEffect(() => {
        generateCaptcha();
        document.title = "Admin Login | CashBolt Media";
    }, []);

    // Handle Login Inputs
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    // Validation
    const validateForm = () => {
        const newErrors = {};
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }
        if (!formData.password) {
            newErrors.password = 'Password is required';
        }
        if (!selectedCaptcha || selectedCaptcha.id !== targetCaptcha.id) {
            newErrors.captcha = 'Please select the correct icon';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // --- Submit Login (Admin) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            const form = document.querySelector('.login-card-unique');
            form.classList.add('shake-anim-unique');
            setTimeout(() => form.classList.remove('shake-anim-unique'), 500);
            return;
        }

        setLoginLoading(true);

        try {
            const response = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'adminLogin', // Changed action for Admin
                email: formData.email,
                password: formData.password
            });

            if (response.data.status === true) {
                toast.success(response.data.message || 'Admin Access Granted');
                // Set Cookie
                document.cookie = `adminToken=${response.data.authToken};expires=${new Date(Date.now()+365*24*60*60*1000).toUTCString()};path=/`;
                
                navigate('../cbMediaAdmin7'); // Or navigate to '/admin/dashboard'
            } else {
                toast.error(response.data.message || 'Invalid Admin Credentials');
                setErrors(prev => ({ ...prev, form: response.data.message }));
                generateCaptcha();
            }
        } catch (error) {
            const msg = error.response?.data?.message || 'Server connection failed.';
            toast.error(msg);
            setErrors(prev => ({ ...prev, form: msg }));
        } finally {
            setLoginLoading(false);
        }
    };

    return (
        <div className="login-page-wrapper-unique">
            
            {showLoader && (
                <Loader 
                    onDataFetched={(data) => setWebsiteData(prev => ({...prev, ...data}))} 
                    onComplete={() => setShowLoader(false)} 
                />
            )}

            <div className="bg-shape-unique shape-1-unique"></div>
            <div className="bg-shape-unique shape-2-unique"></div>

            <div className="login-container-unique">
                
                {/* Branding Side */}
                <div className="login-brand-side-unique">
                    <div className="brand-content-unique">
                        {websiteData.logo && (
                            <img src={websiteData.logo} alt="Logo" className="brand-logo-unique" />
                        )}
                        <h1 className="brand-title-unique animated-title-unique">{websiteData.name}</h1>
                        <p className="brand-tagline-unique">{websiteData.tagline}</p>
                    </div>
                </div>

                {/* Form Side */}
                <div className="login-form-side-unique">
                    <div className="login-card-unique">
                        <div className="form-header-unique">
                            <h2>Admin Access</h2>
                            <p>Enter your master credentials to continue</p>
                        </div>

                        <form onSubmit={handleSubmit} className="login-form-unique">
                            
                            {/* Email */}
                            <div className="form-row-unique">
                                <div className="input-group-unique">
                                    <input 
                                        type="email" name="email" placeholder=" " 
                                        value={formData.email} onChange={handleChange}
                                        className={errors.email ? 'error-border-unique' : ''}
                                    />
                                    <label>Admin Email</label>
                                    {errors.email && <span className="error-msg-unique">{errors.email}</span>}
                                </div>
                            </div>

                            {/* Password */}
                            <div className="form-row-unique">
                                <div className="input-group-unique">
                                    <input 
                                        type="password" name="password" placeholder=" " 
                                        value={formData.password} onChange={handleChange}
                                        className={errors.password ? 'error-border-unique' : ''}
                                    />
                                    <label>Password</label>
                                    {errors.password && <span className="error-msg-unique">{errors.password}</span>}
                                </div>
                            </div>

                            {/* Captcha */}
                            <div className="captcha-container-unique">
                                <p className="captcha-label-unique">
                                    Security Check: Select the <span>{targetCaptcha?.name}</span>
                                </p>
                                <div className="captcha-grid-unique">
                                    {captchaOptions.map((opt) => (
                                        <div 
                                            key={opt.id}
                                            className={`captcha-item-unique ${selectedCaptcha?.id === opt.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedCaptcha(opt)}
                                        >
                                            {opt.symbol}
                                        </div>
                                    ))}
                                </div>
                                {errors.captcha && <div className="error-msg-unique center-unique">{errors.captcha}</div>}
                            </div>

                            {errors.form && <div className="error-msg-unique center-unique">{errors.form}</div>}

                            <button type="submit" className="submit-btn-unique wave-effect-unique" disabled={loginLoading}>
                                {loginLoading ? <span className="btn-loader-unique"></span> : (
                                    <>
                                        <span>SECURE LOGIN</span>
                                        <div className="wave-unique"></div>
                                    </>
                                )}
                            </button>

                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;